import { useState, type ChangeEvent, type FormEvent } from "react";

const FORM_ID = "guayouhui-tipoff-form";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dahanf3p14a1h3mr6h3g";

const categoryOptions = [
  "娱乐圈",
  "网红圈",
  "商业内幕",
  "职场爆料",
  "校园瓜",
  "生活八卦",
  "其他（编辑判断）",
];

const evidenceOptions = [
  "只有文字描述",
  "聊天记录截图",
  "现场照片或视频",
  "录音或通话记录",
  "合同、票据等文件",
  "多方证人可联系",
];

const emptyForm = {
  subject: "",
  category: "",
  happenTime: "",
  evidence: "",
  email: "",
  detail: "",
  cooperation: "愿意",
  agree: false,
};

export default function SubmitForm() {
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const target = event.target;
    const value =
      target instanceof HTMLInputElement && target.type === "checkbox" ? target.checked : target.value;
    setForm((prev) => ({ ...prev, [target.name]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;

    if (form.detail.length > 500) {
      setStatus("error");
      setFormError("线索描述不要超过 500 字，可以先把关键信息写清楚，细节我们在核实阶段再聊。");
      return;
    }

    const payload = new FormData(formEl);

    const guard = String(payload.get("website_alt") ?? "").trim();
    if (guard) {
      setStatus("success");
      setForm(emptyForm);
      return;
    }

    payload.delete("website_alt");
    if (!String(payload.get("agree") ?? "")) {
      payload.set("agree", "未勾选");
    }

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (typeof value === "string") {
        body.append(key, value);
      }
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setForm(emptyForm);
        return;
      }

      setStatus("error");
      setFormError(
        serverMsg && !isSpam
          ? `提交没有成功：${serverMsg}`
          : "提交没有成功，请检查必填项是否填写完整，然后重试。",
      );
    } catch {
      setStatus("error");
      setFormError("网络似乎不太稳定，投稿没有发出去。请稍后重试，你的内容不会被清空。");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-secondary-300 bg-secondary-50 p-8 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-500 text-2xl text-background-50">
          <i className="ri-check-line" aria-hidden="true" />
        </span>
        <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">线索已收到，谢谢你的信任</h2>
        <p className="mt-3 text-sm leading-8 text-foreground-600">
          编辑部会在 48 小时内完成第一轮初筛。如果线索需要补充信息，我们会通过你留下的联系方式（选填）联系你；
          在此之前，你的身份不会被任何第三方知晓。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-400 px-5 py-2.5 text-sm text-secondary-900 transition-colors hover:bg-secondary-100"
        >
          <i className="ri-add-line text-base" aria-hidden="true" />
          再投一条线索
        </button>
      </div>
    );
  }

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <div className="mb-6 flex items-center gap-2 border-b border-background-200 pb-5">
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
          <i className="ri-lock-2-line" aria-hidden="true" />
        </span>
        <div>
          <h2 className="font-heading text-lg font-bold text-foreground-950">匿名投稿表单</h2>
          <p className="mt-1 text-xs text-foreground-500">带 * 为必填项，其余可以留空</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="subject" className="mb-2 block text-sm font-medium text-foreground-800">
            线索标题 <span className="text-accent-600">*</span>
          </label>
          <input
            id="subject"
            name="subject"
            type="text"
            required
            value={form.subject}
            onChange={handleChange}
            placeholder="用一句话概括，例如：某主播承诺的低价货源对不上包装标注"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div>
          <label htmlFor="category" className="mb-2 block text-sm font-medium text-foreground-800">
            所属栏目 <span className="text-accent-600">*</span>
          </label>
          <select
            id="category"
            name="category"
            required
            value={form.category}
            onChange={handleChange}
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          >
            <option value="">请选择栏目</option>
            {categoryOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="happenTime" className="mb-2 block text-sm font-medium text-foreground-800">
            大致发生时间
          </label>
          <input
            id="happenTime"
            name="happenTime"
            type="text"
            value={form.happenTime}
            onChange={handleChange}
            placeholder="例如：今年 8 月中旬"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="evidence" className="mb-2 block text-sm font-medium text-foreground-800">
            你可以提供哪些材料
          </label>
          <select
            id="evidence"
            name="evidence"
            value={form.evidence}
            onChange={handleChange}
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          >
            <option value="">请选择（可不填）</option>
            {evidenceOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="detail" className="mb-2 block text-sm font-medium text-foreground-800">
            线索描述 <span className="text-accent-600">*</span>
          </label>
          <textarea
            id="detail"
            name="detail"
            required
            maxLength={500}
            rows={7}
            value={form.detail}
            onChange={handleChange}
            placeholder="把时间、地点、涉及人物的关键信息写清楚即可，不需要写得很正式。请勿填写未成年人隐私或私人住址。"
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-7 text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
          <p className="mt-2 text-right text-xs text-foreground-500">{form.detail.length} / 500</p>
        </div>

        <div className="sm:col-span-2">
          <span className="mb-2 block text-sm font-medium text-foreground-800">是否愿意配合后续核实</span>
          <div className="flex flex-wrap gap-3">
            {["愿意", "只提供线索，不再联系"].map((option) => (
              <label
                key={option}
                className={`flex cursor-pointer items-center gap-2 rounded-md border px-4 py-2.5 text-sm transition-colors ${
                  form.cooperation === option
                    ? "border-primary-400 bg-primary-50 text-primary-800"
                    : "border-background-300 text-foreground-700 hover:border-primary-300"
                }`}
              >
                <input
                  type="radio"
                  name="cooperation"
                  value={option}
                  checked={form.cooperation === option}
                  onChange={handleChange}
                  className="accent-primary-500"
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground-800">
            联系方式（选填）
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            placeholder="仅用于核实沟通，不会对外显示"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
          <p className="mt-2 text-xs leading-6 text-foreground-500">
            留空也完全可以提交。我们更希望你保护好自己，一切以匿名安全为先。
          </p>
        </div>
      </div>

      <div className="field-guard" aria-hidden="true">
        <label htmlFor="website_alt">官网备用入口</label>
        <input
          id="website_alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <label className="mt-6 flex cursor-pointer items-start gap-3 text-sm leading-7 text-foreground-700">
        <input
          type="checkbox"
          name="agree"
          value="已确认"
          checked={form.agree}
          onChange={handleChange}
          className="mt-1.5 accent-primary-500"
        />
        <span>
          我已阅读并同意站点的内容原则：不提交未成年人隐私、私人住址与无依据的人身攻击性内容。
        </span>
      </label>

      {status === "error" && formError ? (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm leading-7 text-accent-800">
          <i className="ri-error-warning-line mt-0.5 text-base" aria-hidden="true" />
          {formError}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <i
          className={`text-base ${status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}`}
          aria-hidden="true"
        />
        {status === "submitting" ? "正在加密提交…" : "匿名提交线索"}
      </button>

      <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-foreground-500">
        <i className="ri-shield-check-line text-sm text-secondary-600" aria-hidden="true" />
        提交内容仅编辑团队可见，发布前会统一做去标识化处理
      </p>
    </form>
  );
}